import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, ShoppingCart, Disc, Music, Mail } from 'lucide-react';

export function SalesTab({ apiUrl, password }) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/api/admin/sales`, {
        headers: { 'x-admin-password': password }
      });
      if (res.ok) {
        const data = await res.json();
        setSales(data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [apiUrl, password]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handleResendEmail = async (sessionId, email) => {
    const targetEmail = email && email !== 'unknown' ? email : prompt('Enter customer email:');
    if (!targetEmail) return;
    setResending(sessionId);
    try {
      const res = await fetch(`${apiUrl}/api/admin/sales/resend-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
        body: JSON.stringify({ session_id: sessionId, email: targetEmail })
      });
      if (res.ok) {
        alert(`Download email resent to ${targetEmail}`);
      } else {
        const err = await res.json();
        alert(`Failed: ${err.detail || 'Unknown error'}`);
      }
    } catch (e) { alert('Failed to resend email'); }
    setResending(null);
  };

  const revenue = sales.filter(s => s.status === 'paid').reduce((sum, s) => sum + (s.amount || 0), 0);
  const totalSales = sales.length;
  const albums = sales.filter(s => s.product?.includes('Album') || s.product?.includes('album')).length;
  const singles = sales.filter(s => s.product?.includes('Single') || s.product?.includes('single')).length;

  return (
    <div className="space-y-6">
      {/* Sales Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center"><DollarSign size={20} className="text-green-400" /></div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Revenue</p>
              <p className="text-2xl font-bold text-white">${revenue.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600/20 rounded-lg flex items-center justify-center"><ShoppingCart size={20} className="text-teal-400" /></div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Total Sales</p>
              <p className="text-2xl font-bold text-white">{totalSales}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600/20 rounded-lg flex items-center justify-center"><Disc size={20} className="text-purple-400" /></div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Albums</p>
              <p className="text-2xl font-bold text-white">{albums}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gold-500/20 rounded-lg flex items-center justify-center"><Music size={20} className="text-gold-400" /></div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Singles</p>
              <p className="text-2xl font-bold text-white">{singles}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sales-table">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left p-4 text-gray-400 text-sm uppercase tracking-wider">Date</th>
                <th className="text-left p-4 text-gray-400 text-sm uppercase tracking-wider">Product</th>
                <th className="text-left p-4 text-gray-400 text-sm uppercase tracking-wider">Amount</th>
                <th className="text-left p-4 text-gray-400 text-sm uppercase tracking-wider">Status</th>
                <th className="text-left p-4 text-gray-400 text-sm uppercase tracking-wider">Buyer Email</th>
                <th className="text-left p-4 text-gray-400 text-sm uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-400">Loading sales...</td></tr>
              ) : sales.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-gray-400">No sales yet.</td></tr>
              ) : sales.map((sale) => (
                <tr key={sale.session_id || `${sale.date}-${sale.product}`} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                  <td className="p-4 text-gray-300 text-sm">{sale.date ? new Date(sale.date).toLocaleString() : 'N/A'}</td>
                  <td className="p-4 text-white font-medium">{sale.product || 'Unknown'}</td>
                  <td className="p-4 text-green-400 font-medium">${(sale.amount || 0).toFixed(2)}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      sale.status === 'paid' ? 'bg-green-600/20 text-green-400' :
                      sale.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400' :
                      'bg-gray-600/20 text-gray-400'
                    }`}>{sale.status || 'unknown'}</span>
                  </td>
                  <td className="p-4 text-gray-300 text-sm">{sale.email || 'unknown'}</td>
                  <td className="p-4">
                    {sale.status === 'paid' && (
                      <button
                        onClick={() => handleResendEmail(sale.session_id, sale.email)}
                        disabled={resending === sale.session_id}
                        className="px-3 py-1 bg-teal-600/20 text-teal-400 rounded text-xs font-medium hover:bg-teal-600/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                        data-testid={`resend-email-btn`}
                      >
                        <Mail size={12} /> {resending === sale.session_id ? 'Sending...' : 'Resend Email'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
